import { useQuery } from '@tanstack/react-query';
import { AutocompleteElement, useFormContext } from 'react-hook-form-mui';

import Api from '@barboxx/api';
import { PourAvailability } from '@barboxx/models/types';

interface Props {
    disabled?: boolean;
}

interface PourAvailabilityWatchProps {
    pourAvailability: PourAvailability;
}

export function PourAvailabilityInput({ disabled = false }: Props) {
    const { pourAvailability } =
        useFormContext<PourAvailabilityWatchProps>().getValues();
    const pourAvailabilityQueryResult = useQuery({
        queryKey: ['pourAvailability'],
        queryFn: () =>
            Api.Menu.PourAvailability.getPourAvailabilities({
                page: 0,
                size: 1000,
                sort: 'id,asc',
            }),
        initialData: {
            ok: true,
            problem: null,
            originalError: null,
            data: {
                results: pourAvailability ? [pourAvailability] : [],
                total: 1,
                isPaginated: false,
            },
        },
    });

    return (
        <AutocompleteElement
            label="Pour Availability"
            name="pourAvailabilityId"
            loading={pourAvailabilityQueryResult.isLoading}
            multiple={false}
            options={pourAvailabilityQueryResult.data?.data?.results ?? []}
            transform={{
                output(event, value, reason, details) {
                    if (reason === 'selectOption') {
                        return typeof value === 'string' ? value : value?.id;
                    }
                    if (reason === 'removeOption') {
                        return null;
                    }
                    return value;
                },
            }}
            autocompleteProps={{
                fullWidth: true,
                readOnly: disabled,
                disabled: disabled,
                isOptionEqualToValue(
                    option?: PourAvailability,
                    value?: PourAvailability | number | null
                ) {
                    const isNumber = typeof value === 'number';

                    if (isNumber) {
                        return option?.id === value;
                    } else {
                        return option?.id === (value?.id ?? value);
                    }
                },

                getOptionLabel(option) {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return pourAvailabilityName(option);
                },
            }}
        />
    );
}

export function pourAvailabilityName(pourAvailability?: PourAvailability) {
    if (!pourAvailability) {
        return '';
    }

    const containerName =
        pourAvailability.pourFormat?.servingContainer?.name ||
        pourAvailability.pourFormat?.packagingFormat?.name;
    let entityName: string | undefined = undefined;
    switch (pourAvailability.entityType) {
        case 'ProductType':
            entityName = pourAvailability.productType?.name;
            break;
        case 'ProductSubtype':
            entityName = pourAvailability.productSubtype?.name;
            break;
        case 'Category':
            entityName = `${pourAvailability.category?.name} - ${pourAvailability.category?.categoryType}`;
            break;
        case 'Drink':
            entityName = pourAvailability.standardDrink?.name;
            break;
        case 'ProductLine':
            entityName = pourAvailability.productLine?.name;
            break;
        case 'Product':
            entityName = pourAvailability.product?.name;
            break;
    }

    return `${pourAvailability.entityType} (${entityName}) / ${pourAvailability.pourFormat?.pourType?.name} - ${containerName}`;
}
